import { FC ,useRef} from 'react'
import { Modal, Button, message, Table } from 'antd'
import { observer } from 'mobx-react'
import styles from './DownloadDialog.module.less'
import { DownloadDialogProps } from './types'
import { commonUtils } from '@/helpers/commonUtils'
import type { CollapseProps } from 'antd'
import { Collapse } from 'antd'
import { DownloadState } from './DownloadState'
import { api } from '@/helpers/api'

const DownloadDialog: FC<DownloadDialogProps> = observer((props) => {
  const { visible, onClose, materials, hasUnitChapter, total } = props
  const state = useRef(new DownloadState()).current
  
  // 存储选中行的完整数据（key为url，value为完整对象）
  const selectedMaterialsRef = useRef<Map<string, DataModels.goods.Attachment>>(new Map())


  // 下载单个文件
  const handleDownload = async (material: DataModels.goods.Attachment) => {
     try {
        // 增加下载记录埋点
        api.goods.addDownloadRecord({
          goodsId: material.goodsId,
          goodsType: material?.goodsType,
          materialId: material.materialId,
        })
      } catch (error) {
        console.log('getDownloadRecord', error)
      }
    commonUtils.downloadFile(material.url as string)
  }

  // 根据 url 从 materials 中查找完整的 Attachment 对象
  const findMaterialByUrl = (url: string): DataModels.goods.Attachment | null => {
    if (!materials) return null
    
    const searchInList = (list: DataModels.goods.Attachment[]): DataModels.goods.Attachment | null => {
      for (const item of list) {
        if (item.url === url) return item
        if (item.children && item.children.length > 0) {
          const found = searchInList(item.children)
          if (found) return found
        }
      }
      return null
    }
    
    return searchInList(materials)
  }

  // 处理表格选择变化
  const handleRowSelectionChange = (selectedKeys: React.Key[]) => {
    state.setSelectedRowKeys(selectedKeys)
    
    // 同步更新完整对象映射
    const newMap = new Map<string, DataModels.goods.Attachment>()
    selectedKeys.forEach(key => {
      const url = key as string
      const material = findMaterialByUrl(url)
      if (material) {
        newMap.set(url, material)
      }
    })
    selectedMaterialsRef.current = newMap
  }

  // 下载全部
  const handleDownloadAll = async () => {
    if(state.selectedRowKeys.length === 0) {
      message.error('请先选择文件后再进行批量下载')
      return
    }
    if(state.selectedRowKeys.length > 10) {
      message.error('批量下载最大支持10个文件，请重新选择')
      return
    }

    const totalFiles = state.selectedRowKeys.length
    let successCount = 0
    let failCount = 0

    try {
      for (let i = 0; i < state.selectedRowKeys.length; i++) {
        const url = state.selectedRowKeys[i] as string
        
        // 从映射中获取完整数据
        const material = selectedMaterialsRef.current.get(url)
        
        if (!material) {
          console.warn(`未找到 url 对应的完整数据: ${url}`)
          failCount++
          continue
        }
        
        try {
          // 上报下载记录
          try {
            await api.goods.addDownloadRecord({
              goodsId: material.goodsId,
              goodsType: material.goodsType,
              materialId: material.materialId,
            })
          } catch (error) {
            console.error('上报下载记录失败:', error)
          }
          
          // 执行下载
          await commonUtils.downloadFileWithDelay(url, 2500)
          successCount++
          
          // 显示进度提示
          if (i < totalFiles - 1) {
            message.loading({
              content: `正在下载 ${successCount}/${totalFiles} 个文件...`,
              key: 'batchDownload',
              duration: 0
            })
          }
        } catch (error) {
          console.error(`第 ${i + 1} 个文件下载失败:`, error)
          failCount++
        }
      }
      
      // 清除loading提示
      message.destroy('batchDownload')
      
      // 显示最终结果
      if (failCount === 0) {
        // message.success(`成功下载 ${successCount} 个文件`)
      } else {
        message.warning(`下载完成：成功 ${successCount} 个，失败 ${failCount} 个`)
      }
    } catch (error) {
      console.error('批量下载失败:', error)
      message.destroy('batchDownload')
      // message.error('批量下载失败，请重试')
    } finally {
      onClose()
      state.selectedRowKeys = []
      selectedMaterialsRef.current.clear()
    }
  }

  const onCancel = () => {
    onClose()
    state.selectedRowKeys = []
    selectedMaterialsRef.current.clear()
  }

  const transformChapterListToItems = (
    chapter: DataModels.goods.Attachment,
  ): CollapseProps['items'] => {
    return [
      {
        key: chapter.size,
        label: chapter.chapterTitle,
        collapsible: 'icon',
        children: (
          <div>
            {chapter.children && chapter.children.length > 0 && (
              <Table
                dataSource={chapter.children}
                rowKey='url'
                rowSelection={{
                  selectedRowKeys: state.selectedRowKeys,
                  onChange: handleRowSelectionChange,
                }}
                pagination={false}
                size='small'
                columns={[
                  {
                    title: '名称',
                    dataIndex: 'title',
                    key: 'title',
                    ellipsis: true,
                  },
                  {
                    title: '大小',
                    dataIndex: 'size',
                    key: 'size',
                    ellipsis: true,
                    width: 100,
                  },
                  {
                    title: '操作',
                    dataIndex: 'goodsId',
                    key: 'operation',
                    width: 100,
                    render: (_: any, record: DataModels.goods.Attachment) => (
                      <Button
                        type='link'
                        size='small'
                        danger
                        onClick={() => handleDownload(record)}
                      >
                        下载
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </div>
        ),
      },
    ]
  }

  return (
    <Modal
      title={<div className={styles.dialogTitle}>相关资料</div>}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      className={styles.downloadDialog}
      closeIcon={<span className={styles.closeIcon}>×</span>}
      maskClosable={false}
    >
      <div className={styles.dialogContent}>
        {
          materials && materials.length === 0 && (
            <div className={styles.empty}>暂无资料</div>
          )
        }
        {
          hasUnitChapter ? (
            materials?.map((item) => {
              return <Collapse items={transformChapterListToItems(item)}></Collapse>
            })
          ) :
            <>
              <Table
                dataSource={materials}
                rowKey="url"
                rowSelection={{
                  selectedRowKeys: state.selectedRowKeys,
                  onChange: handleRowSelectionChange,
                }}
                pagination={false}
                size="small"
                columns={
                  [
                    {
                      title: '名称',
                      dataIndex: 'title',
                      key: 'title',
                      ellipsis: true,
                    },
                    {
                      title: '大小',
                      dataIndex: 'size',
                      key: 'size',
                      ellipsis: true,
                      width: 100,
                    },
                    {
                      title: '操作',
                      dataIndex: "goodsId",
                      key: "goodsId",
                      width: 120,
                      render: (_, record) => {
                        return <>
                          <button
                            className={styles.downloadBtn}
                            onClick={() => handleDownload(record)}
                          >
                            下载
                          </button>
                        </>
                      }
                    }

                  ]}
              />

            </>
        }

      </div>
      <div className={styles.dialogFooter}>
        <div className={styles.footerLeft}>
          <button className={styles.feedbackBtn} ></button>
        </div>
        <div className={styles.footerRight}>
          {state.selectedRowKeys.length>0 && (<span>已选择{state.selectedRowKeys.length}个文件</span>)} 
          <span className={styles.fileCount}>共{total}个文件</span>
          <span className={styles.downloadAllBtn} onClick={handleDownloadAll}>
            批量下载
          </span>
        </div>
      </div>
    </Modal>
  )
})

export default DownloadDialog
